import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from "flowbite-react";
import { useEffect, useState } from "react";
import { HiHome, HiPencil, HiTrash } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import { getPlaylists } from "../../services/radio";
import AddPlaylistModal from "./AddPlaylistModal";
import EditPlaylistModal from "./EditPlaylistModal";
import DeletePlaylistModal from "./DeletePlaylistModal";
import { ToastContainer } from "react-toastify";

const RadioListPage = () => {
  const [playlists, setPlaylists] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const navigate = useNavigate();

  const loadPlaylists = async () => {
    setLoading(true);
    const res = await getPlaylists({ page: 1, take: 100 });
    setPlaylists(res.data.results.items || []);
    setLoading(false);
  };

  const handleDeletePlaylist = (playlist: any) => {
    setSelectedPlaylist({ id: playlist.id, title: playlist.title });
    setDeleteModalOpen(true);
  };

  useEffect(() => {
    loadPlaylists();
  }, []);

  return (
    <NavbarSidebarLayout isFooter={false}>
      <div className="block items-center justify-between border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:flex">
        <div className="mb-1 w-full">
          <div className="mb-4">
            <Breadcrumb className="mb-4">
              <BreadcrumbItem href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">Home</span>
                </div>
              </BreadcrumbItem>
              <BreadcrumbItem href="/radio">Radio</BreadcrumbItem>
            </Breadcrumb>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
              Radio Playlists
            </h1>
          </div>
          <div className="sm:flex">
            <div className="ml-auto flex items-center space-x-2 sm:space-x-3">
              <AddPlaylistModal onCreated={loadPlaylists} />
            </div>
          </div>
        </div>
      </div>
      <div className="mb-8 flex flex-col">
        <div className="overflow-hidden">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden shadow-sm">
              <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                <TableHead className="bg-gray-100 dark:bg-gray-700">
                  <TableHeadCell>Playlist</TableHeadCell>
                  <TableHeadCell>Actions</TableHeadCell>
                </TableHead>
                <TableBody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={2} className="px-6 py-4 text-center">
                        <div role="status" className="size-full text-center">
                          <svg
                            aria-hidden="true"
                            className="mx-auto my-10 size-8 animate-spin fill-blue-600 text-center text-gray-200 dark:text-gray-600"
                            viewBox="0 0 100 101"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                              fill="currentColor"
                            />
                            <path
                              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                              fill="currentFill"
                            />
                          </svg>
                          <span className="sr-only">Loading...</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    playlists.map((playlist: any) => (
                      <TableRow
                        key={playlist.id}
                        className="hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <TableCell className="whitespace-nowrap px-6 py-4 text-base font-normal text-gray-900 dark:text-white">
                          <div className="flex items-center">
                            <span>{playlist.title}</span>
                            {playlist.is_default && (
                              <Badge color="info" className="ml-2 text-xs">
                                Default
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              color="primary"
                              onClick={() =>
                                navigate(`/radio/manage/${playlist.id}`)
                              }
                            >
                              <div className="flex items-center gap-x-2">
                                <HiPencil className="text-lg" />
                                Manage Items
                              </div>
                            </Button>
                            <EditPlaylistModal
                              playlist={playlist}
                              onUpdated={loadPlaylists}
                            />
                            <Button
                              color="failure"
                              onClick={() => handleDeletePlaylist(playlist)}
                            >
                              <div className="flex items-center gap-x-2">
                                <HiTrash className="text-lg" />
                                Delete
                              </div>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar
        newestOnTop={false}
        theme="dark"
      />
      {deleteModalOpen && selectedPlaylist && (
        <DeletePlaylistModal
          playlist={selectedPlaylist}
          isOpen={deleteModalOpen}
          setIsOpen={setDeleteModalOpen}
          onDeleted={loadPlaylists}
        />
      )}
    </NavbarSidebarLayout>
  );
};

export default RadioListPage;
